import type { DoorDirection, RoomLayoutShape } from "./types";
import {
  getTileHeight,
  isBoundaryEdge,
  resolveLayoutWalls,
  TILE_WORLD_HEIGHT_UNIT,
} from "./room-layout";

export const FRAME_ROTATION_NORTH = 0 as const;
export const FRAME_ROTATION_WEST = 90 as const;
export const FRAME_ROTATION_SOUTH = 180 as const;
export const FRAME_ROTATION_EAST = 270 as const;
export type FrameRotation =
  | typeof FRAME_ROTATION_NORTH
  | typeof FRAME_ROTATION_WEST
  | typeof FRAME_ROTATION_SOUTH
  | typeof FRAME_ROTATION_EAST;

// Legacy wall slot heights kept for backward compatibility with already-placed frames.
export const FRAME_WORLD_HEIGHTS = [1.3, 2.15] as const;
export const FRAME_WALL_INSET = 0.07;
export const FRAME_TILE_HEIGHT_UNIT = TILE_WORLD_HEIGHT_UNIT;
export const FRAME_BASE_WALL_HEIGHT = 3.5;
const FRAME_WALL_SLOT_FACTORS: [number, number] = [0.38, 0.67];
const PLACEABLE_FRAME_DIRECTIONS: Array<"north" | "west"> = ["north", "west"];
const FRAME_EDGE_MARGIN = 0.03;
const FRAME_PLANE_SNAP_TOLERANCE = 0.45;

export const FRAME_MIN_AREA = 0.22;
export const FRAME_DEFAULT_AREA = 0.95;
export const FRAME_MAX_AREA = 2.4;
export const FRAME_MIN_ASPECT_RATIO = 0.2;
export const FRAME_MAX_ASPECT_RATIO = 5;
export const FRAME_RESIZE_FACTOR = 1.15;
const FRAME_FALLBACK_WIDTH = 0.98;
const FRAME_FALLBACK_HEIGHT = 0.98;

export interface FrameDimensions {
  width: number;
  height: number;
}

export interface FrameSlotPlacement {
  x: number;
  y: number;
  z: number;
  rotation: FrameRotation;
}

export interface FrameWorldTransform {
  x: number;
  y: number;
  z: number;
  rotationY: number;
}

interface FrameWallSegment {
  alongMin: number;
  alongMax: number;
  baseY: number;
}

export interface FrameWallRun {
  direction: "north" | "west";
  plane: number;
  alongMin: number;
  alongMax: number;
  topY: number;
  segments: FrameWallSegment[];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function directionToRotationY(direction: DoorDirection): number {
  switch (direction) {
    case "north":
      return 0;
    case "west":
      return Math.PI / 2;
    case "south":
      return Math.PI;
    case "east":
      return -Math.PI / 2;
    default:
      return 0;
  }
}

export function clampFrameAspectRatio(aspectRatio?: number): number {
  if (!Number.isFinite(aspectRatio)) {
    return 1;
  }
  return clamp(aspectRatio as number, FRAME_MIN_ASPECT_RATIO, FRAME_MAX_ASPECT_RATIO);
}

export function clampFrameArea(area?: number): number {
  if (!Number.isFinite(area)) {
    return FRAME_DEFAULT_AREA;
  }
  return clamp(area as number, FRAME_MIN_AREA, FRAME_MAX_AREA);
}

export function frameDimensionsFromAspectRatio(
  aspectRatio: number,
  area = FRAME_DEFAULT_AREA
): FrameDimensions {
  const safeAspect = clampFrameAspectRatio(aspectRatio);
  const safeArea = clampFrameArea(area);
  const width = Math.sqrt(safeArea * safeAspect);
  const height = Math.sqrt(safeArea / safeAspect);
  return { width, height };
}

export function clampFrameDimensions(
  width?: number,
  height?: number,
  aspectRatioHint?: number
): FrameDimensions {
  const hasWidth = Number.isFinite(width) && (width as number) > 0;
  const hasHeight = Number.isFinite(height) && (height as number) > 0;
  if (!hasWidth || !hasHeight) {
    const fallbackAspect = clampFrameAspectRatio(aspectRatioHint);
    return frameDimensionsFromAspectRatio(fallbackAspect);
  }

  const rawWidth = width as number;
  const rawHeight = height as number;
  const aspect = clampFrameAspectRatio(rawWidth / rawHeight);
  const area = clampFrameArea(rawWidth * rawHeight);
  return frameDimensionsFromAspectRatio(aspect, area);
}

export function scaleFrameDimensions(
  width: number | undefined,
  height: number | undefined,
  scaleFactor: number
): FrameDimensions {
  const base = clampFrameDimensions(width, height);
  if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
    return base;
  }
  return clampFrameDimensions(base.width * scaleFactor, base.height * scaleFactor);
}

export function frameDirectionFromRotation(
  rotation: number
): DoorDirection | null {
  switch (rotation) {
    case FRAME_ROTATION_NORTH:
      return "north";
    case FRAME_ROTATION_WEST:
      return "west";
    case FRAME_ROTATION_SOUTH:
      return "south";
    case FRAME_ROTATION_EAST:
      return "east";
    default:
      return null;
  }
}

export function frameRotationFromDirection(direction: DoorDirection): FrameRotation {
  switch (direction) {
    case "north":
      return FRAME_ROTATION_NORTH;
    case "west":
      return FRAME_ROTATION_WEST;
    case "south":
      return FRAME_ROTATION_SOUTH;
    case "east":
      return FRAME_ROTATION_EAST;
    default:
      return FRAME_ROTATION_NORTH;
  }
}

export function resolveFrameWallRuns(
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles" | "door" | "walls">,
  options?: {
    wallBaseHeight?: number;
  }
): FrameWallRun[] {
  const wallBaseHeight = options?.wallBaseHeight ?? FRAME_BASE_WALL_HEIGHT;
  const maxTileHeight = frameMaxTileHeightForLayout(layout);
  const wallTopY = maxTileHeight * FRAME_TILE_HEIGHT_UNIT + wallBaseHeight;

  const grouped = new Map<string, Array<{
    direction: "north" | "west";
    plane: number;
    alongMin: number;
    alongMax: number;
    baseY: number;
  }>>();
  for (const edge of resolveLayoutWalls(layout)) {
    if (
      edge.x === layout.door.x &&
      edge.z === layout.door.z &&
      edge.direction === layout.door.direction
    ) {
      continue;
    }
    if (!PLACEABLE_FRAME_DIRECTIONS.includes(edge.direction as "north" | "west")) {
      continue;
    }

    const baseY = getTileHeight(layout as RoomLayoutShape, edge.x, edge.z) * FRAME_TILE_HEIGHT_UNIT;
    if (edge.direction === "north") {
      const plane = edge.z - 0.5 + FRAME_WALL_INSET;
      const key = `north:${plane.toFixed(4)}`;
      const items = grouped.get(key) ?? [];
      items.push({
        direction: "north",
        plane,
        alongMin: edge.x - 0.5,
        alongMax: edge.x + 0.5,
        baseY,
      });
      grouped.set(key, items);
      continue;
    }

    const plane = edge.x - 0.5 + FRAME_WALL_INSET;
    const key = `west:${plane.toFixed(4)}`;
    const items = grouped.get(key) ?? [];
    items.push({
      direction: "west",
      plane,
      alongMin: edge.z - 0.5,
      alongMax: edge.z + 0.5,
      baseY,
    });
    grouped.set(key, items);
  }

  const runs: FrameWallRun[] = [];
  for (const items of grouped.values()) {
    if (items.length === 0) continue;
    items.sort((a, b) => a.alongMin - b.alongMin);
    let currentSegments: FrameWallSegment[] = [];
    let currentDirection: "north" | "west" = items[0].direction;
    let currentPlane = items[0].plane;
    let currentMin = items[0].alongMin;
    let currentMax = items[0].alongMax;

    const flushRun = () => {
      if (currentSegments.length === 0) return;
      runs.push({
        direction: currentDirection,
        plane: currentPlane,
        alongMin: currentMin,
        alongMax: currentMax,
        topY: wallTopY,
        segments: currentSegments,
      });
    };

    for (const item of items) {
      if (currentSegments.length === 0) {
        currentDirection = item.direction;
        currentPlane = item.plane;
        currentMin = item.alongMin;
        currentMax = item.alongMax;
        currentSegments = [
          { alongMin: item.alongMin, alongMax: item.alongMax, baseY: item.baseY },
        ];
        continue;
      }

      const contiguous = item.alongMin <= currentMax + 0.0001;
      if (contiguous) {
        currentMax = Math.max(currentMax, item.alongMax);
        currentSegments.push({
          alongMin: item.alongMin,
          alongMax: item.alongMax,
          baseY: item.baseY,
        });
        continue;
      }

      flushRun();
      currentDirection = item.direction;
      currentPlane = item.plane;
      currentMin = item.alongMin;
      currentMax = item.alongMax;
      currentSegments = [
        { alongMin: item.alongMin, alongMax: item.alongMax, baseY: item.baseY },
      ];
    }
    flushRun();
  }

  return runs;
}

export function normalizeFrameSlotPlacement(
  input: {
    x: number;
    y?: number;
    z: number;
    rotation?: number;
  },
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles" | "door" | "walls">,
  options?: {
    frameWidth?: number;
    frameHeight?: number;
    wallBaseHeight?: number;
  }
): FrameSlotPlacement | null {
  const direction = frameDirectionFromRotation(input.rotation ?? NaN);
  if (direction !== "north" && direction !== "west") {
    return null;
  }

  const dimensions = clampFrameDimensions(options?.frameWidth, options?.frameHeight);
  const halfWidth = dimensions.width / 2;
  const halfHeight = dimensions.height / 2;
  const alongRaw = direction === "north" ? input.x : input.z;
  const planeRaw = direction === "north" ? input.z : input.x;

  const runs = resolveFrameWallRuns(layout, {
    wallBaseHeight: options?.wallBaseHeight,
  }).filter((run) => run.direction === direction);
  if (runs.length === 0) {
    return null;
  }

  let best:
    | {
        run: FrameWallRun;
        along: number;
        planeDelta: number;
        score: number;
      }
    | null = null;
  for (const run of runs) {
    const minAlong = run.alongMin + halfWidth + FRAME_EDGE_MARGIN;
    const maxAlong = run.alongMax - halfWidth - FRAME_EDGE_MARGIN;
    if (maxAlong < minAlong) continue;

    const along = clamp(alongRaw, minAlong, maxAlong);
    const planeDelta = Math.abs(planeRaw - run.plane);
    const alongDelta = Math.abs(alongRaw - along);
    const score = planeDelta * 2 + alongDelta;
    if (!best || score < best.score) {
      best = {
        run,
        along,
        planeDelta,
        score,
      };
    }
  }

  if (!best || best.planeDelta > FRAME_PLANE_SNAP_TOLERANCE) {
    return null;
  }

  const coverageMin = best.along - halfWidth - FRAME_EDGE_MARGIN;
  const coverageMax = best.along + halfWidth + FRAME_EDGE_MARGIN;
  const coveredSegments = best.run.segments.filter(
    (segment) => segment.alongMax > coverageMin && segment.alongMin < coverageMax
  );
  if (coveredSegments.length === 0) {
    return null;
  }

  const baseY = coveredSegments.reduce(
    (highest, segment) => Math.max(highest, segment.baseY),
    Number.NEGATIVE_INFINITY
  );
  const minY = baseY + halfHeight + FRAME_EDGE_MARGIN;
  const maxY = best.run.topY - halfHeight - FRAME_EDGE_MARGIN;
  if (!Number.isFinite(minY) || !Number.isFinite(maxY) || maxY < minY) {
    return null;
  }

  const desiredY = Number.isFinite(input.y) ? (input.y as number) : (minY + maxY) / 2;
  const y = clamp(desiredY, minY, maxY);

  if (direction === "north") {
    return {
      x: best.along,
      y,
      z: best.run.plane,
      rotation: frameRotationFromDirection(direction),
    };
  }

  return {
    x: best.run.plane,
    y,
    z: best.along,
    rotation: frameRotationFromDirection(direction),
  };
}

export function isFramePlacementEdge(
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles" | "door" | "walls">,
  x: number,
  z: number,
  direction: DoorDirection
): boolean {
  if (!PLACEABLE_FRAME_DIRECTIONS.includes(direction as "north" | "west")) {
    return false;
  }
  if (!isBoundaryEdge(layout as RoomLayoutShape, x, z, direction)) {
    return false;
  }
  if (
    x === layout.door.x &&
    z === layout.door.z &&
    direction === layout.door.direction
  ) {
    return false;
  }
  const walls = resolveLayoutWalls(layout as RoomLayoutShape);
  return walls.some(
    (edge) => edge.x === x && edge.z === z && edge.direction === direction
  );
}

function isLegacyFrameSlot(
  slot: Pick<FrameSlotPlacement, "x" | "y" | "z">
): boolean {
  return (
    Number.isInteger(slot.x) &&
    Number.isInteger(slot.z) &&
    Number.isInteger(slot.y) &&
    slot.y >= 0 &&
    slot.y < FRAME_WORLD_HEIGHTS.length
  );
}

export function frameSlotToWorldTransform(
  slot: Pick<FrameSlotPlacement, "x" | "y" | "z" | "rotation">,
  tileHeight = 0,
  options?: {
    maxTileHeight?: number;
    wallBaseHeight?: number;
  }
): FrameWorldTransform {
  const direction = frameDirectionFromRotation(slot.rotation) ?? "north";

  if (!isLegacyFrameSlot(slot)) {
    return {
      x: slot.x,
      y: slot.y,
      z: slot.z,
      rotationY: directionToRotationY(direction),
    };
  }

  const yIndex = Math.max(
    0,
    Math.min(FRAME_WALL_SLOT_FACTORS.length - 1, Math.round(slot.y))
  );
  const localBaseY = tileHeight * FRAME_TILE_HEIGHT_UNIT;
  const maxTileHeight = options?.maxTileHeight ?? tileHeight;
  const wallBaseHeight = options?.wallBaseHeight ?? FRAME_BASE_WALL_HEIGHT;
  const localWallHeight =
    Math.max(0, maxTileHeight - tileHeight) * FRAME_TILE_HEIGHT_UNIT +
    wallBaseHeight;
  const slotHeights = FRAME_WALL_SLOT_FACTORS.map(
    (factor) => localWallHeight * factor
  ) as [number, number];
  const y = localBaseY + slotHeights[yIndex];

  switch (direction) {
    case "north":
      return {
        x: Math.round(slot.x),
        y,
        z: Math.round(slot.z) - 0.5 + FRAME_WALL_INSET,
        rotationY: 0,
      };
    case "west":
      return {
        x: Math.round(slot.x) - 0.5 + FRAME_WALL_INSET,
        y,
        z: Math.round(slot.z),
        rotationY: Math.PI / 2,
      };
    case "south":
      return {
        x: Math.round(slot.x),
        y,
        z: Math.round(slot.z) + 0.5 - FRAME_WALL_INSET,
        rotationY: Math.PI,
      };
    case "east":
      return {
        x: Math.round(slot.x) + 0.5 - FRAME_WALL_INSET,
        y,
        z: Math.round(slot.z),
        rotationY: -Math.PI / 2,
      };
    default:
      return {
        x: Math.round(slot.x),
        y,
        z: Math.round(slot.z),
        rotationY: 0,
      };
  }
}

export function frameHeightSlotFromWorldY(
  worldY: number,
  wallBaseHeight = FRAME_BASE_WALL_HEIGHT
): 0 | 1 {
  const slotHeights = FRAME_WALL_SLOT_FACTORS.map(
    (factor) => wallBaseHeight * factor
  ) as [number, number];
  const switchY = (slotHeights[0] + slotHeights[1]) / 2;
  return worldY >= switchY ? 1 : 0;
}

export function framePlacementDirections(): DoorDirection[] {
  return [...PLACEABLE_FRAME_DIRECTIONS];
}

export function frameTileHeightForLayout(
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles" | "door" | "walls">,
  x: number,
  z: number
): number {
  return getTileHeight(layout as RoomLayoutShape, x, z);
}

export function frameMaxTileHeightForLayout(
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles">
): number {
  let maxHeight = 0;
  for (let row = 0; row < layout.height; row++) {
    for (let col = 0; col < layout.width; col++) {
      const tile = layout.tiles[row]?.[col];
      if (!tile?.walkable) continue;
      if (tile.height > maxHeight) {
        maxHeight = tile.height;
      }
    }
  }
  return maxHeight;
}
